import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import generatePicker from 'antd/es/date-picker/generatePicker';
import dateFnsGenerateConfig from 'rc-picker/lib/generate/dateFns';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DAY_MONTH_YEAR_FORMAT_WITH_SLASHES } from 'constants/global';
import { FormItem } from 'interfaces/shared.interface';
import { ErrorMessage, InputIcon } from 'shared-components';
import { cn } from 'utils/global';

const DatePicker = generatePicker(dateFnsGenerateConfig);

export type Props = {
  label?: string;
  error?: string;
  name: string;
  control: Control;
  required?: boolean;
  icon?: IconDefinition;
  placeHolder?: string;
  mode?: 'date' | 'month' | 'year';
  format?: string;
} & Omit<FormItem, 'component'>;

const DatePickerInput = ({
  className,
  name,
  dataCy,
  control,
  labelClassName,
  inputClassName,
  label,
  disabled,
  required,
  icon,
  error,
  mode,
  format,
  placeHolder = 'SelectDate',
}: Props) => {
  const { t } = useTranslation();

  return (
    <div data-cy={dataCy} className={className}>
      {label && (
        <label
          className={cn('block text-neutral-900 dark:text-white-00 mb-2 text-bodyMd-semibold', labelClassName, {
            'opacity-70': disabled,
          })}
        >
          {t(label)}
          {label && required && ' *'}
        </label>
      )}
      <div className="flex items-center">
        <InputIcon icon={icon} />
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <DatePicker
              name={field.name}
              value={field.value ? new Date(field.value) : ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              format={format || DAY_MONTH_YEAR_FORMAT_WITH_SLASHES}
              picker={mode}
              className={cn(
                'py-2 px-2.5 rounded border-neutral-200 dark:border-neutral-800 active:!border-primary-500 !text-white-00 dark:!text-black-900 !text-bodyMd date-picker bg-white-00 dark:bg-black-900 hover:bg-neutral-50 dark:hover:bg-neutral-900',
                { '!border-error': !!error },
                { '!rounded-l-none': icon },
                inputClassName
              )}
              placeholder={t(placeHolder)}
            />
          )}
        />
      </div>
      {error && <ErrorMessage dataCy="error-message">{t(error)}</ErrorMessage>}
    </div>
  );
};

export default DatePickerInput;
